/**
 * Soulumo Foundations · Sleep
 *
 * Route: /foundations/sleep
 *
 * A fully Soulumo-themed port of Reclaim's Sleep Biohacking area
 * (Overview · Protocol · Checklist · Sleep Installations · Tools).
 *
 * Reuses the existing /api/biohacking/sleep/* endpoints verbatim so
 * all data stays in one place; only the visual language changes to
 * navy + champagne + Playfair/Manrope. One-tap sleep-journey CTAs
 * from the Reclaim version are intentionally omitted — those live
 * inside Foundations as separate audios ("Rest 7m", "Night 8h")
 * surfaced from CareForMePage.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Sunrise, Sunset, Bed, Sparkles, Check,
  ShoppingBag, ChevronRight, Loader2, Play, Pause,
} from "lucide-react";
import SoulumoWord from "../../components/SoulumoWord";
import { TOK, SERIF, SANS } from "./foundationsTokens";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ Authorization: `Bearer ${getToken()}` });

const BLOCK_ICON = { morning: Sunrise, daytime: Sun, evening: Sunset, bedroom: Bed };

const TABS = [
  { id: "overview",      label: "Overview" },
  { id: "protocol",      label: "Protocol" },
  { id: "checklist",     label: "Checklist" },
  { id: "installations", label: "Installations" },
  { id: "tools",         label: "Tools" },
];

export default function FoundationsSleepPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="foundations-sleep-page"
    >
      {/* soft halo */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-18%", left: "50%", transform: "translateX(-50%)",
          width: 620, height: 620,
          background: "radial-gradient(circle, rgba(224,169,122,0.22) 0%, rgba(224,169,122,0.05) 32%, transparent 68%)",
          filter: "blur(32px)",
        }}
        aria-hidden
      />

      <div className="max-w-md mx-auto px-5 pt-7 pb-24 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => nav("/foundations/care-for-me")}
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS,
              fontSize: "0.62rem", letterSpacing: "0.28em",
              background: "transparent", minHeight: 44,
            }}
            data-testid="foundations-sleep-back"
          >
            ← Back
          </button>
          <SoulumoWord size="xs" />
          <div style={{ width: 40 }} />
        </div>

        {/* Title */}
        <div className="text-center mt-10">
          <div
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem",
              letterSpacing: "0.42em", textTransform: "uppercase",
            }}
          >
            Foundations · Sleep
          </div>
          <h1
            className="mt-4 italic leading-tight"
            style={{
              color: TOK.cream, fontFamily: SERIF,
              fontSize: "clamp(1.6rem, 6vw, 2.1rem)", fontWeight: 300,
            }}
            data-testid="foundations-sleep-title"
          >
            Prepare the night.
          </h1>
          <p
            className="mt-3 max-w-sm mx-auto"
            style={{
              color: TOK.creamDim, fontFamily: SANS,
              fontSize: "0.92rem", lineHeight: 1.6,
            }}
          >
            The simple, steady system that turns one good night into a
            rhythm. Return as often as you need.
          </p>
        </div>

        {/* Tabs */}
        <nav
          className="mt-8 -mx-5 px-5 flex gap-1.5 overflow-x-auto no-scrollbar"
          data-testid="foundations-sleep-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`foundations-sleep-tab-${t.id}`}
              className="px-3.5 py-1.5 rounded-full whitespace-nowrap transition"
              style={{
                background: tab === t.id ? "rgba(224,169,122,0.18)" : "rgba(245,236,217,0.04)",
                border: `1px solid ${tab === t.id ? TOK.gold : TOK.cardBorder}`,
                color: tab === t.id ? TOK.cream : TOK.creamMist,
                fontFamily: SANS, fontSize: "0.72rem",
                letterSpacing: "0.1em", minHeight: 36,
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {/* Panels */}
        <main className="mt-6">
          {tab === "overview"      && <OverviewPanel />}
          {tab === "protocol"      && <ProtocolPanel />}
          {tab === "checklist"     && <ChecklistPanel />}
          {tab === "installations" && <InstallationsPanel />}
          {tab === "tools"         && <ToolsPanel />}
        </main>
      </div>
    </div>
  );
}

// ── Shared primitives ──────────────────────────────────────────────
function Skel() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="animate-spin" size={22} style={{ color: TOK.creamMist }} />
    </div>
  );
}

function Card({ children, testid, style }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl p-5"
      style={{
        background: TOK.cardBg,
        border: `1px solid ${TOK.cardBorder}`,
        ...(style || {}),
      }}
    >
      {children}
    </div>
  );
}

// ── Overview ───────────────────────────────────────────────────────
function OverviewPanel() {
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(false);
  const refresh = useCallback(() => {
    fetch(`${API}/api/biohacking/sleep/overview`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!data) return <Skel />;
  const tiles = data.tiles || {};
  const recovery = tiles.recovery_score || {};

  return (
    <div className="space-y-4">
      {/* Sleep Score */}
      <Card testid="foundations-sleep-score">
        <div
          className="text-center"
          style={{ fontFamily: SANS }}
        >
          <div
            style={{
              color: TOK.goldSoft, fontSize: "0.6rem",
              letterSpacing: "0.32em", textTransform: "uppercase",
            }}
          >
            Sleep Score
          </div>
          <div
            className="my-2"
            style={{
              fontFamily: SERIF, fontWeight: 300,
              fontSize: "4rem", color: TOK.cream, lineHeight: 1,
            }}
          >
            {data.sleep_score}
          </div>
          <div
            style={{
              color: TOK.creamMist, fontSize: "0.72rem",
              fontStyle: "italic", fontFamily: SERIF,
            }}
          >
            {data.tracked_days} of the last 30 days tracked
          </div>
        </div>
      </Card>

      {/* Recovery score tile */}
      <button
        type="button"
        onClick={() => setRating(true)}
        className="w-full text-left rounded-2xl p-5 transition"
        style={{
          background: "linear-gradient(135deg, rgba(224,169,122,0.12), rgba(224,169,122,0.03))",
          border: `1px solid ${TOK.goldMist}`,
          minHeight: 72,
        }}
        data-testid="foundations-sleep-recovery-tile"
      >
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div
              style={{
                color: TOK.goldSoft, fontSize: "0.6rem",
                letterSpacing: "0.3em", textTransform: "uppercase",
                fontFamily: SANS,
              }}
            >
              Recovery score
            </div>
            <div
              className="mt-1"
              style={{
                color: TOK.cream, fontFamily: SERIF, fontWeight: 300,
                fontSize: "1.8rem", lineHeight: 1,
              }}
            >
              {recovery.value === "—" ? "—" : recovery.value}
              <span
                style={{ color: TOK.creamFaint, fontSize: "0.9rem" }}
              > / 10</span>
            </div>
            <div
              className="mt-1 italic"
              style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.78rem" }}
            >
              {recovery.logged_today
                ? `You logged ${recovery.today_score}/10 this morning. Tap to update.`
                : "How restored do you feel this morning? Tap to log."}
            </div>
          </div>
          <Sparkles size={20} style={{ color: TOK.gold }} />
        </div>
      </button>

      {/* Streak + consistency tiles */}
      <div className="grid grid-cols-2 gap-3">
        {["bedtime_consistency", "morning_light_consistency", "evening_routine_consistency", "streak"].map((k) => {
          const t = tiles[k]; if (!t) return null;
          return (
            <div
              key={k}
              className="rounded-xl p-4"
              style={{
                background: TOK.cardBg,
                border: `1px solid ${TOK.cardBorder}`,
                minHeight: 92,
              }}
            >
              <div
                style={{
                  color: TOK.goldSoft, fontSize: "0.55rem",
                  letterSpacing: "0.28em", textTransform: "uppercase",
                  fontFamily: SANS,
                }}
              >
                {t.label}
              </div>
              <div
                className="mt-1.5"
                style={{
                  color: TOK.cream, fontFamily: SERIF, fontWeight: 300,
                  fontSize: "1.7rem", lineHeight: 1,
                }}
              >
                {t.value}
              </div>
              <div
                className="mt-1"
                style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.7rem" }}
              >
                {t.unit}
              </div>
            </div>
          );
        })}
      </div>

      <p
        className="text-center italic pt-2"
        style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
      >
        Most of us already know what to do. Soulumo helps you actually do it.
      </p>

      {rating && (
        <RecoveryScoreModal
          current={recovery.today_score}
          onClose={() => setRating(false)}
          onSaved={() => { setRating(false); refresh(); }}
        />
      )}
    </div>
  );
}

function RecoveryScoreModal({ current, onClose, onSaved }) {
  const [score, setScore] = useState(current || 7);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/biohacking/sleep/recovery-score`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ score }),
      });
      onSaved();
    } catch (_) { onSaved(); } finally { setSaving(false); }
  };
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-5"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: TOK.ink, border: `1px solid ${TOK.goldMist}` }}
        data-testid="foundations-sleep-recovery-modal"
      >
        <div
          style={{
            color: TOK.goldSoft, fontSize: "0.6rem",
            letterSpacing: "0.32em", textTransform: "uppercase",
            fontFamily: SANS,
          }}
        >
          Recovery score
        </div>
        <h3
          className="mt-2 italic leading-tight"
          style={{ color: TOK.cream, fontFamily: SERIF, fontWeight: 300, fontSize: "1.5rem" }}
        >
          How restored do you feel this morning?
        </h3>
        <p
          className="mt-2 italic"
          style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
        >
          Tune in to the body, not the wearable. 1 — wrecked · 10 — radiant.
        </p>
        <div className="flex items-center justify-between gap-1 mt-5 mb-4" data-testid="foundations-sleep-recovery-scale">
          {[1,2,3,4,5,6,7,8,9,10].map((n) => (
            <button
              key={n}
              onClick={() => setScore(n)}
              data-testid={`foundations-sleep-recovery-${n}`}
              className="rounded-md transition"
              style={{
                width: 30, height: 40,
                background: score === n ? TOK.gold : "rgba(245,236,217,0.05)",
                color: score === n ? TOK.ink : TOK.creamDim,
                fontFamily: SANS, fontSize: "0.85rem",
                border: `1px solid ${score === n ? TOK.gold : TOK.cardBorder}`,
              }}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg"
            style={{
              background: "transparent",
              border: `1px solid ${TOK.cardBorder}`,
              color: TOK.creamMist, fontFamily: SANS, fontSize: "0.8rem",
            }}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="foundations-sleep-recovery-save"
            className="flex-1 py-2.5 rounded-lg disabled:opacity-60"
            style={{
              background: TOK.gold, color: TOK.ink,
              fontFamily: SANS, fontSize: "0.8rem", fontWeight: 500,
            }}
          >
            {saving ? "Saving…" : `Log ${score}/10`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Protocol ───────────────────────────────────────────────────────
function ProtocolPanel() {
  const [proto, setProto] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/protocol`).then((r) => r.json())
      .then((d) => setProto(d.protocol)).catch(() => {});
  }, []);
  if (!proto) return <Skel />;
  return (
    <div className="space-y-4">
      {["morning","daytime","evening","bedroom"].map((bk) => {
        const block = proto[bk]; const Icon = BLOCK_ICON[bk] || Sun;
        return (
          <Card key={bk} testid={`foundations-sleep-protocol-${bk}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={18} style={{ color: TOK.gold }} />
              <h3 style={{ fontFamily: SERIF, fontSize: "1.2rem", color: TOK.cream, fontWeight: 400 }}>
                {block.title}
              </h3>
            </div>
            <div
              className="italic mb-4"
              style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
            >
              {block.tagline}
            </div>
            <div className="space-y-3.5">
              {block.items.map((it) => (
                <div
                  key={it.key}
                  className="pl-3"
                  style={{ borderLeft: `2px solid ${TOK.goldMist}` }}
                >
                  <div
                    style={{ color: TOK.cream, fontFamily: SANS, fontSize: "0.9rem" }}
                  >
                    {it.title}
                  </div>
                  <div
                    className="mt-0.5"
                    style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.78rem", lineHeight: 1.5 }}
                  >
                    {it.why}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── Checklist ──────────────────────────────────────────────────────
function ChecklistPanel() {
  const [data, setData] = useState(null);
  const [env, setEnv]   = useState(null);

  const loadAll = useCallback(() => {
    fetch(`${API}/api/biohacking/sleep/checklist/today`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setData).catch(() => {});
    fetch(`${API}/api/biohacking/sleep/environment`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setEnv).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleRoutine = async (key, checked) => {
    setData((d) => d ? { ...d, items: d.items.map((it) => it.key === key ? { ...it, checked } : it) } : d);
    try {
      await fetch(`${API}/api/biohacking/sleep/checklist/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ key, checked }),
      });
      loadAll();
    } catch (_) { loadAll(); }
  };

  const toggleEnv = async (key, checked) => {
    setEnv((e) => e ? { ...e, items: e.items.map((it) => it.key === key ? { ...it, checked } : it) } : e);
    try {
      await fetch(`${API}/api/biohacking/sleep/environment/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ key, checked }),
      });
      loadAll();
    } catch (_) { loadAll(); }
  };

  if (!data || !env) return <Skel />;

  const row = (checked, onClick, label, hint, extra, testid) => (
    <button
      key={testid}
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-start gap-3 p-3.5 rounded-xl text-left transition"
      style={{
        background: checked ? "rgba(224,169,122,0.12)" : "rgba(245,236,217,0.04)",
        border: `1px solid ${checked ? TOK.goldMist : TOK.cardBorder}`,
        minHeight: 56,
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{
          width: 20, height: 20, borderRadius: 6,
          background: checked ? TOK.gold : "transparent",
          border: `1px solid ${checked ? TOK.gold : TOK.creamFaint}`,
        }}
      >
        {checked && <Check size={14} style={{ color: TOK.ink }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ color: checked ? TOK.cream : TOK.creamDim, fontFamily: SANS, fontSize: "0.9rem" }}>
          {label}
        </div>
        {hint && (
          <div
            className="mt-0.5"
            style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.72rem", lineHeight: 1.45 }}
          >
            {hint}
          </div>
        )}
      </div>
      {extra && (
        <span
          className="uppercase flex-shrink-0"
          style={{
            color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.58rem",
            letterSpacing: "0.24em",
          }}
        >
          {extra}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Progress card */}
      <Card testid="foundations-sleep-checklist-progress">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div style={{ color: TOK.goldSoft, fontSize: "0.55rem", letterSpacing: "0.3em", textTransform: "uppercase", fontFamily: SANS }}>
              Today
            </div>
            <div style={{ color: TOK.cream, fontFamily: SANS, fontSize: "0.95rem", marginTop: 2 }}>
              {data.completed} of {data.total} routine
            </div>
          </div>
          <div style={{ color: TOK.gold, fontFamily: SERIF, fontWeight: 300, fontSize: "1.8rem" }}>
            {Math.round(data.completion_ratio * 100)}%
          </div>
        </div>
        <div
          className="mt-3 pt-3 flex items-center justify-between gap-4"
          style={{ borderTop: `1px solid ${TOK.divider}` }}
        >
          <div>
            <div style={{ color: TOK.goldSoft, fontSize: "0.55rem", letterSpacing: "0.3em", textTransform: "uppercase", fontFamily: SANS }}>
              Environment
            </div>
            <div style={{ color: TOK.cream, fontFamily: SANS, fontSize: "0.9rem", marginTop: 2 }}>
              {env.score} of {env.out_of} dialled in
            </div>
          </div>
          <div style={{ color: TOK.cream, fontFamily: SERIF, fontWeight: 300, fontSize: "1.5rem" }}>
            {env.score}<span style={{ color: TOK.creamFaint, fontSize: "0.9rem" }}>/{env.out_of}</span>
          </div>
        </div>
      </Card>

      {/* Today's routine */}
      <section>
        <header className="flex items-baseline justify-between mb-2">
          <h3 style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.68rem", letterSpacing: "0.26em", textTransform: "uppercase" }}>
            Today&apos;s routine
          </h3>
          <span style={{ color: TOK.creamMist, fontFamily: SERIF, fontStyle: "italic", fontSize: "0.72rem" }}>
            resets daily
          </span>
        </header>
        <div className="space-y-1.5" data-testid="foundations-sleep-checklist-list">
          {data.items.map((it) => (
            row(
              it.checked,
              () => toggleRoutine(it.key, !it.checked),
              it.label,
              null,
              it.block,
              `foundations-sleep-check-${it.key}`,
            )
          ))}
        </div>
      </section>

      {/* Environment */}
      <section data-testid="foundations-sleep-environment-section">
        <header className="flex items-baseline justify-between mb-2">
          <h3 style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.68rem", letterSpacing: "0.26em", textTransform: "uppercase" }}>
            Sleep environment
          </h3>
          <span style={{ color: TOK.creamMist, fontFamily: SERIF, fontStyle: "italic", fontSize: "0.72rem" }}>
            set once · tangible
          </span>
        </header>
        <div className="space-y-1.5">
          {env.items.map((it) => (
            row(
              it.checked,
              () => toggleEnv(it.key, !it.checked),
              it.label,
              it.hint,
              null,
              `foundations-sleep-env-${it.key}`,
            )
          ))}
        </div>
      </section>

      <TonightsTally
        dialled={data.completed + env.score}
        total={data.total + env.out_of}
      />
    </div>
  );
}

function TonightsTally({ dialled, total }) {
  let tone;
  if (total === 0) tone = "Tonight is a fresh canvas.";
  else if (dialled === 0)           tone = "Tomorrow is a fresh dial.";
  else if (dialled <= total * 0.25) tone = "A start. The day softens from here.";
  else if (dialled <= total * 0.55) tone = "Coming together. Stay with it.";
  else if (dialled <= total * 0.80) tone = "A steady rhythm tonight.";
  else if (dialled < total)         tone = "Beautiful — almost full alignment.";
  else                              tone = "Every dial turned. Sleep deeply.";
  return (
    <div
      data-testid="foundations-sleep-tonights-tally"
      className="rounded-2xl p-5 text-center"
      style={{
        background: "linear-gradient(135deg, rgba(224,169,122,0.1), rgba(13,31,58,0.1))",
        border: `1px solid ${TOK.goldMist}`,
      }}
    >
      <div style={{ color: TOK.goldSoft, fontSize: "0.58rem", letterSpacing: "0.32em", textTransform: "uppercase", fontFamily: SANS }}>
        Tonight
      </div>
      <div
        className="mt-1 italic"
        style={{ color: TOK.cream, fontFamily: SERIF, fontWeight: 300, fontSize: "1.4rem" }}
      >
        {dialled} of {total} dialled in
      </div>
      <p
        className="mt-1 italic"
        style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
      >
        {tone}
      </p>
    </div>
  );
}

// ── Installations ──────────────────────────────────────────────────
function InstallationsPanel() {
  const [list, setList] = useState(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/installations`).then((r) => r.json())
      .then((d) => setList(d.installations || [])).catch(() => {});
  }, []);
  if (!list) return <Skel />;
  if (list.length === 0) {
    return (
      <div
        className="text-center py-10 italic"
        style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.9rem" }}
      >
        Installations are rendering — check back in a minute.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {list.map((it) => (
        <Card key={it.slug} testid={`foundations-sleep-install-${it.slug}`}>
          <div className="flex items-start gap-3">
            <div
              className="flex-shrink-0 rounded-full p-2"
              style={{ background: "rgba(224,169,122,0.14)", border: `1px solid ${TOK.goldMist}`, color: TOK.gold }}
            >
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div style={{ color: TOK.cream, fontFamily: SERIF, fontSize: "1.05rem" }}>
                {it.title}
              </div>
              <div
                className="italic mt-0.5"
                style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.78rem" }}
              >
                {it.subtitle}
              </div>
              <div
                className="mt-2"
                style={{ color: TOK.creamFaint, fontFamily: SANS, fontSize: "0.7rem" }}
              >
                {Math.round((it.duration_s || 0) / 60)} min · your voice · soft healing music
              </div>
            </div>
          </div>
          <SleepAudioPlayer
            src={`${API}${it.audio_url}`}
            playing={playing === it.slug}
            onToggle={() => setPlaying(playing === it.slug ? null : it.slug)}
          />
        </Card>
      ))}
    </div>
  );
}

function SleepAudioPlayer({ src, playing, onToggle }) {
  const [audio] = useState(() => typeof Audio !== "undefined" ? new Audio() : null);
  useEffect(() => {
    if (!audio) return;
    audio.src = src;
    audio.preload = "none";
    audio.loop = false;
    return () => { audio.pause(); };
  }, [audio, src]);
  useEffect(() => {
    if (!audio) return;
    if (playing) audio.play().catch(() => {});
    else audio.pause();
  }, [audio, playing]);
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-full transition"
      style={{
        background: playing ? TOK.gold : "rgba(224,169,122,0.14)",
        color: playing ? TOK.ink : TOK.cream,
        border: `1px solid ${TOK.gold}`,
        fontFamily: SANS, fontSize: "0.75rem", letterSpacing: "0.18em",
        textTransform: "uppercase",
        minHeight: 44,
      }}
    >
      {playing ? <><Pause size={16} /> Pause</> : <><Play size={16} /> Play installation</>}
    </button>
  );
}

// ── Tools ──────────────────────────────────────────────────────────
function ToolsPanel() {
  const [tools, setTools] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/tools`).then((r) => r.json())
      .then((d) => setTools(d.tools || [])).catch(() => {});
  }, []);
  if (!tools) return <Skel />;
  return (
    <div className="space-y-3">
      <div
        className="text-center italic pb-1"
        style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.82rem" }}
      >
        Gentle recommendations — affiliate partnerships coming soon.
      </div>
      {tools.map((t) => (
        <div
          key={t.slug}
          className="flex items-center gap-3 rounded-2xl p-4"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid={`foundations-sleep-tool-${t.slug}`}
        >
          <div
            className="flex-shrink-0 rounded-full p-2"
            style={{ background: "rgba(224,169,122,0.14)", border: `1px solid ${TOK.goldMist}`, color: TOK.gold }}
          >
            <ShoppingBag size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div style={{ color: TOK.cream, fontFamily: SANS, fontSize: "0.9rem" }}>
              {t.name}
            </div>
            <div
              className="mt-0.5"
              style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.75rem", lineHeight: 1.45 }}
            >
              {t.description}
            </div>
          </div>
          <div
            style={{ color: TOK.creamFaint, fontFamily: SANS, fontSize: "0.72rem" }}
            className="flex-shrink-0"
          >
            {t.price_band}
          </div>
          <ChevronRight size={16} style={{ color: TOK.creamFaint }} className="flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}
